import { STATUS_WAITING_FOR } from '@kontentino/kontentino-constants/Posts';
import {
  ActivityIndicator,
  AvatarGroup,
  Badge,
  Icon,
  mergeTailwindClasses,
} from '@kontentino/ui';
import React, { FC } from 'react';
import WorkflowStatusIcon from './WorkflowStatusIcon';
import clsx from 'clsx';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import { useTranslation } from 'react-i18next';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';

type Props = {
  status: number;
  assignedUsers?: {
    id: number;
    name: string;
    avatar?: {
      src: string | undefined;
    };
  }[];
  variant?: 'plain' | 'subtle';
  className?: string;
  showChevron?: boolean;
  isLoading?: boolean;
};

const WorkflowStatusBadge: FC<Props> = ({
  status,
  assignedUsers,
  variant = 'subtle',
  className,
  showChevron,
  isLoading,
}) => {
  const { postStatusVariant } = usePostsLayoutState();
  const { t } = useTranslation();
  return (
    <Badge
      data-cy={`post-header-status-${status}`}
      className={mergeTailwindClasses(
        clsx(`tw-gap-2 tw-whitespace-nowrap tw-text-grayscale-180`, {
          [`tw-bg-status-${postStatusVariant}-${status}-secondary`]:
            variant === 'subtle',
          [`tw-bg-transparent tw-p-0 tw-text-status-${postStatusVariant}-${status}-primary`]:
            variant === 'plain',
        }),
        className,
      )}
    >
      {isLoading ? (
        <ActivityIndicator className="!tw-border-t-primary-100" />
      ) : (
        <WorkflowStatusIcon status={status} />
      )}
      <span className="tw-hidden lg:tw-flex">
        {status === STATUS_WAITING_FOR && !!assignedUsers?.length
          ? t('assignedTo')
          : t(`workflowStatus.label.${status}`)}
      </span>
      <span className="tw-flex tw-select-none lg:tw-hidden">{`${t(
        `workflowStatus.shortLabel.${status}`,
      )}`}</span>
      {!!assignedUsers?.length && (
        <AvatarGroup
          className="tw-hidden lg:tw-flex"
          avatars={assignedUsers.map((user) => ({
            ...user,
            src: user.avatar?.src,
          }))}
          limit={2}
        />
      )}
      {showChevron && (
        <Icon
          icon={faChevronDown}
          size="sm"
          className="tw-text-grayscale-100 tw-transition-transform tw-duration-200 group-data-[state=open]/status-badge:tw-rotate-180"
        />
      )}
    </Badge>
  );
};

export default WorkflowStatusBadge;
